
import { memo, useMemo } from 'react';

import { AccordionTrigger } from '@/components/core/Accordion';
import { useTranslation } from '@/hooks/useTranslation';
import { LayoutSection } from '@/types/DataModel';

import { SectionValidationPopover } from './SectionValidationPopover';

interface SectionAccordionTriggerProps {
    section: LayoutSection;
    translatedSectionName?: string;
    id?: string;
}

const _SectionAccordionTrigger = ({ section, translatedSectionName, id }: SectionAccordionTriggerProps) => {

    const { t } = useTranslation();

    // Memoize the translated section name to prevent unnecessary re-translations
    const finalTranslatedSectionName = useMemo(() => {
        if (translatedSectionName) {
            return translatedSectionName;
        }
        const translated = t(`section.${section.name}`);
        return translated === `section.${section.name}` ? (section.label || section.name) : translated;
    }, [translatedSectionName, section.name, section.label, t]);

    // Memoize the section attribute names to prevent unnecessary array recreations
    const sectionAttributeNames = useMemo(() =>
        section.attributes.map(attribute => attribute.name),
    [section.attributes]
    );

    return (
        <AccordionTrigger className="tw-relative tw-bg-[#f7f7f8] tw-px-4 tw-py-2 tw-mt-3 tw-rounded" id={id}>
            <div className="tw-flex tw-items-center tw-gap-2">
                <a className='tw-text-t-primary tw-text-t-lg header-section-title'>{finalTranslatedSectionName}</a>

                <div className="tw-flex tw-items-center lg:tw-gap-1">
                    <SectionValidationPopover sectionAttributeNames={sectionAttributeNames} type="errors" />

                    <SectionValidationPopover sectionAttributeNames={sectionAttributeNames} type="warnings" />
                </div>
            </div>
        </AccordionTrigger>
    );
};

_SectionAccordionTrigger.DisplayName = 'SectionAccordionTrigger';

// Memoize the component to prevent unnecessary re-renders
const SectionAccordionTrigger = memo(_SectionAccordionTrigger);

export { SectionAccordionTrigger };
